import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useSocket } from '../../contexts/SocketContext'
import { api } from '../../utils/api'
import toast from 'react-hot-toast'

const ChatHost = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { joinChat, toggleCommentVisibility: socketToggleVisibility, dismissComment: socketDismissComment, onNewComments, onCommentCategorized, onCommentVisibilityUpdated, onCommentDismissed } = useSocket()
  
  const [chatConfig, setChatConfig] = useState(null)
  const [comments, setComments] = useState({
    IMPORTANT: [],
    QUESTION: [],
    COMMENT: [],
    UNCATEGORIZED: []
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchChatConfig()
    joinChat(id)
    
    // Set up real-time listeners
    onNewComments((data) => {
      if (data.chatId === id) {
        const newComments = data.comments
        const groupedNewComments = {
          IMPORTANT: newComments.filter(comment => comment.category === 'IMPORTANT'),
          QUESTION: newComments.filter(comment => comment.category === 'QUESTION'),
          COMMENT: newComments.filter(comment => comment.category === 'COMMENT'),
          UNCATEGORIZED: newComments.filter(comment => !comment.category || comment.category === null)
        }
        
        setComments(prev => ({
          IMPORTANT: [...groupedNewComments.IMPORTANT, ...prev.IMPORTANT],
          QUESTION: [...groupedNewComments.QUESTION, ...prev.QUESTION],
          COMMENT: [...groupedNewComments.COMMENT, ...prev.COMMENT],
          UNCATEGORIZED: [...groupedNewComments.UNCATEGORIZED, ...prev.UNCATEGORIZED]
        }))
        
        toast.success(`${newComments.length} new comments received`)
      }
    })
    
    onCommentCategorized((data) => {
      if (data.chatId === id) {
        setComments(prev => {
          const newComments = { ...prev }
          // Remove from old category
          Object.keys(newComments).forEach(category => {
            newComments[category] = newComments[category].filter(comment => comment.id !== data.commentId)
          })
          // Add to new category
          const comment = prev.IMPORTANT.find(c => c.id === data.commentId) ||
                         prev.QUESTION.find(c => c.id === data.commentId) ||
                         prev.COMMENT.find(c => c.id === data.commentId) ||
                         prev.UNCATEGORIZED.find(c => c.id === data.commentId)
          if (comment) {
            newComments[data.category] = [...newComments[data.category], { ...comment, category: data.category }]
          }
          return newComments
        })
      }
    })
    
    onCommentVisibilityUpdated((data) => {
      if (data.chatId === id) {
        setComments(prev => {
          const newComments = { ...prev }
          Object.keys(newComments).forEach(category => {
            newComments[category] = newComments[category].map(comment =>
              comment.id === data.commentId ? { ...comment, isVisible: data.isVisible } : comment
            )
          })
          return newComments
        })
      }
    })
    
    onCommentDismissed((data) => {
      if (data.chatId === id) {
        setComments(prev => {
          const newComments = { ...prev }
          Object.keys(newComments).forEach(category => {
            newComments[category] = newComments[category].filter(comment => comment.id !== data.commentId)
          })
          return newComments
        })
      }
    })
    
    return () => {
      // Cleanup listeners
    }
  }, [id, onNewComments, onCommentCategorized, onCommentVisibilityUpdated, onCommentDismissed])

  const fetchChatConfig = async () => {
    try {
      const response = await api.get(`/chat/${id}`)
      setChatConfig(response.data.chatConfig)
      fetchComments()
    } catch (error) {
      console.error('Failed to fetch chat config:', error)
      toast.error('Failed to load chat configuration')
      navigate('/chat')
    } finally {
      setLoading(false)
    }
  }

  const fetchComments = async () => {
    try {
      const response = await api.get(`/chat/${id}/comments`)
      const allComments = response.data.comments
      
      // Group comments by category
      const groupedComments = {
        IMPORTANT: allComments.filter(comment => comment.category === 'IMPORTANT'),
        QUESTION: allComments.filter(comment => comment.category === 'QUESTION'),
        COMMENT: allComments.filter(comment => comment.category === 'COMMENT'),
        UNCATEGORIZED: allComments.filter(comment => !comment.category || comment.category === null)
      }
      
      setComments(groupedComments)
    } catch (error) {
      console.error('Failed to fetch comments:', error)
      toast.error('Failed to load comments')
    }
  }

  const showComment = (commentId) => {
    // Update local state immediately for instant visual feedback
    setComments(prev => {
      const newComments = { ...prev }
      Object.keys(newComments).forEach(category => {
        newComments[category] = newComments[category].map(comment =>
          comment.id === commentId ? { ...comment, isVisible: true } : comment
        )
      })
      return newComments
    })
    // Send to socket for persistence
    socketToggleVisibility(id, commentId, true)
    toast.success('Comment shown on broadcast')
  }

  const dismissComment = (commentId) => {
    // Update local state immediately for instant visual feedback
    setComments(prev => {
      const newComments = { ...prev }
      Object.keys(newComments).forEach(category => {
        newComments[category] = newComments[category].filter(comment => comment.id !== commentId)
      })
      return newComments
    })
    // Send to socket for persistence
    socketDismissComment(id, commentId)
    toast.success('Comment dismissed')
  }

  const CommentCard = ({ comment, category }) => (
    <div className={`p-4 rounded border-l-4 ${
      category === 'IMPORTANT' ? 'border-red-500' :
      category === 'QUESTION' ? 'border-blue-500' :
      category === 'COMMENT' ? 'border-green-500' :
      'border-gray-500'
    } ${
      comment.amountMicros ? 'bg-yellow-900/20 border-yellow-500' : 'bg-[#1c1c1c]'
    }`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            {comment.authorThumbnailUrl && (
              <img
                src={comment.authorThumbnailUrl}
                alt={comment.authorName}
                className="w-8 h-8 rounded-full object-cover"
                onError={(e) => {
                  console.warn('Thumbnail failed to load for', comment.authorName);
                  e.target.style.display = 'none';
                }}
              />
            )}
            <div>
              <div className="text-white font-medium">
                {comment.authorName}
                {comment.amountMicros && (
                  <span className="ml-2 text-yellow-400 font-bold">
                    ${(comment.amountMicros / 1000000).toFixed(2)}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="text-gray-300 text-sm">
            {comment.commentText}
          </div>
        </div>
        
        <div className="flex gap-2 ml-4">
          <button
            onClick={() => showComment(comment.id)}
            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 text-xs font-medium transition-colors"
          >
            SHOW
          </button>
          <button
            onClick={() => dismissComment(comment.id)}
            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 text-xs font-medium transition-colors"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="bg-[#1c1c1c] text-white p-8 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="spinner w-12 h-12 mx-auto mb-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          <p className="text-white">Loading chat host...</p>
        </div>
      </div>
    )
  }

  if (!chatConfig) {
    return (
      <div className="bg-[#1c1c1c] text-white p-8 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-white text-xl">Chat configuration not found</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-black text-white min-h-screen">
      <div className="px-6 pb-6">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/chat')}
            className="text-white hover:text-gray-300 transition-colors flex items-center gap-2 font-avenir"
          >
            <span>&lt;</span>
            BACK
          </button>
        </div>

        {/* Title and Subtitle Section */}
        <div className="mb-8">
          <div className="flex items-center gap-8 mb-6">
            <div className="flex-1">
              <div className="text-white text-2xl font-bold mb-2">
                {chatConfig.name || 'CHAT HOST'}
              </div>
              <div className="text-white text-lg">
                Broadcast Comment Display
              </div>
            </div>
            <div className="flex items-center gap-4 bg-[#1c1c1c] p-6">
              <button
                onClick={fetchComments}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 font-medium transition-colors"
              >
                Refresh Comments
              </button>
              <Link
                to={`/chat/${id}/control`}
                className="bg-transparent border border-white text-white hover:bg-white hover:text-black px-6 py-3 font-medium transition-colors"
              >
                Go to Control View
              </Link>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-[#1c1c1c] p-6">
          <h2 className="text-white text-xl text-center font-bold mb-6">Categorized Comments</h2>
          
          {/* Comments Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Important Comments */}
            <div className="bg-[#323232] border-2 border-white p-6">
              <h3 className="text-white text-lg font-bold mb-4 text-center">IMPORTANT</h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {comments.IMPORTANT.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-400">No important comments</p>
                  </div>
                ) : (
                  comments.IMPORTANT.map((comment) => (
                    <CommentCard
                      key={comment.id}
                      comment={comment}
                      category="IMPORTANT"
                    />
                  ))
                )}
              </div>
            </div>

            {/* Question Comments */}
            <div className="bg-[#323232] border-2 border-white p-6">
              <h3 className="text-white text-lg font-bold mb-4 text-center">QUESTIONS</h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {comments.QUESTION.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-400">No questions</p>
                  </div>
                ) : (
                  comments.QUESTION.map((comment) => (
                    <CommentCard
                      key={comment.id}
                      comment={comment}
                      category="QUESTION"
                    />
                  ))
                )}
              </div>
            </div>

            {/* General Comments */}
            <div className="bg-[#323232] border-2 border-white p-6">
              <h3 className="text-white text-lg font-bold mb-4 text-center">COMMENTS</h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {comments.COMMENT.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-400">No comments</p>
                  </div>
                ) : (
                  comments.COMMENT.map((comment) => (
                    <CommentCard
                      key={comment.id}
                      comment={comment}
                      category="COMMENT"
                    />
                  ))
                )}
              </div>
            </div>

            {/* Uncategorized Comments */}
            <div className="bg-[#323232] border-2 border-white p-6">
              <h3 className="text-white text-lg font-bold mb-4 text-center">UNCATEGORIZED</h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {comments.UNCATEGORIZED.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-400">No uncategorized comments</p>
                  </div>
                ) : (
                  comments.UNCATEGORIZED.map((comment) => (
                    <CommentCard
                      key={comment.id}
                      comment={comment}
                      category="UNCATEGORIZED"
                    />
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-[#323232] border-2 border-white p-6 mt-8">
            <h3 className="text-white text-lg font-bold mb-4 text-center">HOST INSTRUCTIONS</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-gray-300">
              <div>
                <h3 className="font-medium text-white mb-2">SHOW Button</h3>
                <p>Displays the comment on the broadcast output for viewers to see.</p>
              </div>
              <div>
                <h3 className="font-medium text-white mb-2">✕ Button</h3>
                <p>Dismisses the comment from the host view without showing it.</p>
              </div>
              <div>
                <h3 className="font-medium text-white mb-2">Categories</h3>
                <p>Comments are organized by importance: Important, Questions, and General comments.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ChatHost
